/**
 * Staged KYC proposals (TASK-617/618).
 *
 * Feeders (meetings, documents, chat) stage proposals server-side; the KYC
 * workspace lists them and the RM accepts/dismisses. A transition accept
 * runs the whole server-side cascade (record_transition + needs_reconfirm
 * flags + follow-up tasks) in one call.
 */

import { httpClient } from '../lib/httpClient';

export interface TransitionPayload {
  closes?: { valid_to?: string };
  opens: { fields: Record<string, unknown>; valid_from: string };
}

export interface FieldPayload {
  field_path: string;
  value: unknown;
}

export interface StagedProposalSourceRef {
  meeting_id?: string;
  utterance_seq?: number;
  ts?: number;
  quote?: string;
  document_id?: string;
  /** TASK-928: the page a document claim was read from, and the email it came in on. */
  page?: number;
  email_id?: string;
}

export interface StagedProposal {
  id: string;
  client_id: string;
  proposal_type: 'field' | 'transition';
  domain: string;
  payload: TransitionPayload | FieldPayload;
  confidence: number | null;
  // TASK-928: `email` (migration 245) — email triage stages life events here.
  source_kind: 'meeting' | 'document' | 'chat' | 'email';
  source_ref: StagedProposalSourceRef;
  status: 'pending' | 'accepted' | 'dismissed';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string;
}

export interface StagedProposalList {
  client_id: string;
  proposals: StagedProposal[];
  /** Per-domain "Affects:" chips mirroring the server-side accept cascade. */
  transition_affects: Record<string, string[]>;
}

export interface TransitionAcceptResult {
  proposal_id: string;
  transition?: unknown;
  needs_reconfirm?: string[];
  follow_up_task_ids?: string[];
  risk_answers_invalidated?: string[];
  applied?: unknown;
}

export async function listStagedProposals(clientId: string, status = 'pending') {
  return httpClient.get<StagedProposalList>(`/api/kyc-proposals/${clientId}`, {
    params: { status },
  });
}

export async function getPendingProposalCounts() {
  return httpClient.get<{ counts: Record<string, number>; total: number }>(
    '/api/kyc-proposals/pending-counts'
  );
}

export async function acceptStagedProposal(
  proposalId: string,
  overrides?: { valid_from?: string }
) {
  return httpClient.post<TransitionAcceptResult>(
    `/api/kyc-proposals/${proposalId}/accept`,
    { overrides }
  );
}

export async function dismissStagedProposal(proposalId: string, note?: string) {
  return httpClient.post<{ proposal_id: string; status: string }>(
    `/api/kyc-proposals/${proposalId}/dismiss`,
    { note }
  );
}
